import * as RadixToggle from '@radix-ui/react-toggle';
import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef, useId, useState } from 'react';

import { cn } from '~/lib/cn';
import type { VariantProps } from '~/lib/variants';
import { variants } from '~/lib/variants';

const wrapperVariants = variants({
  base: 'relative flex items-center justify-start gap-md',
  variants: {
    variant: {
      formField: 'h-[3rem]',
    },
  },
});

export type ToggleProps = RadixToggle.ToggleProps &
  VariantProps<typeof wrapperVariants> & {
    // Shown when the Toggle is active/pressed/checked
    activeLabel?: ReactNode;
    // Shown when the Toggle is not active/pressed/checked
    inactiveLabel?: ReactNode;
  };

export const Toggle = forwardRef(
  (
    {
      className,
      activeLabel,
      inactiveLabel,
      pressed,
      onPressedChange,
      defaultPressed,
      id: defaultId,
      variant,
      ...props
    }: ToggleProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const id = useId();

    // Readonly mode, this state is used only to set correct text, it's not passed to RadixToggle
    // and does not affect the toggle's functionality.
    const [localPressed, setLocalPressed] = useState(
      pressed ?? defaultPressed ?? false,
    );

    const handlePressedChange = (newPressed: boolean) => {
      setLocalPressed(newPressed);
      onPressedChange?.(newPressed);
    };

    return (
      <div className={cn(wrapperVariants({ variant }))}>
        <RadixToggle.Toggle
          id={defaultId ?? id}
          className={cn(
            'relative',
            'bg-emphasis-default',
            'rounded-full',
            'w-[3rem]',
            'h-lg',
            'p-xs',
            'transition-colors',
            'after:content-[""]',
            'after:rounded-full',
            'after:bg-default-inverse',
            'after:w-md',
            'after:h-md',
            'after:absolute',
            'after:inset-xs',
            'after:transition-transform',
            'aria-pressed:enabled:bg-primary-default',
            'aria-pressed:after:translate-x-lg',
            'disabled:bg-level-05',
            'disabled:cursor-not-allowed',
            'disabled:after:bg-level-01',
            className,
          )}
          ref={ref}
          pressed={pressed}
          onPressedChange={handlePressedChange}
          defaultPressed={defaultPressed}
          {...props}
        />

        {activeLabel || inactiveLabel ? (
          <label
            htmlFor={defaultId ?? id}
            className="flex w-auto text-body-sm text-white"
          >
            {localPressed ? activeLabel : inactiveLabel}
          </label>
        ) : null}
      </div>
    );
  },
);

Toggle.displayName = 'Toggle';
